import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { initReactI18next } from 'react-i18next';

import App from './App';
import AppLoadingScreen from './AppLoadingScreen.tsx';
import analytics from './modules/analytics';
import chat from './modules/chat';
import { initAppInsights } from './modules/core/analytics/applicationInsights';
import * as Toast from './modules/core/components/Toast.tsx';

import 'regenerator-runtime/runtime';
import { merge } from 'lodash';
import i18n, { Resource } from 'i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { ApolloProvider } from '@apollo/client/react';
import * as Sentry from '@sentry/react';

import { client } from './modules/core/utils/authentication';
import { modules } from './modules/core/utils/modules';
import customer from './modules/customer';
import forms from './modules/forms';
import login from './modules/login';
import masterdata from './modules/masterdata';
import assistants from './modules/products/assistants';
import propertyBills from './modules/products/db_immo';
import delivery_notes from './modules/products/delivery_notes';
import invoices from './modules/products/invoices';
import list_of_services from './modules/products/list_of_services';
import orderConfirmations from './modules/products/order_confirmations';
import orders from './modules/products/orders';
import rfqs from './modules/products/rfqs';
import universal from './modules/products/universal';
import voice_agents from './modules/products/voice_agents';
import mailboxes from './modules/products/mailboxes';
import teams from './modules/teams';
import users from './modules/users';

import { setDefaultOptions } from 'date-fns';
import { cs, de, enUS, es, fr, hr, hu, it, ja, ko, pl, ro, sk, sl, sr } from 'date-fns/locale';

// Tailwind registers every source file as a CSS dependency, so Vite never falls back to a full reload
// when this entry is hot-updated; re-running it would mount a second React root on #root.
import.meta.hot?.dispose(() => window.location.reload());

if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/service-worker.js');
}

// Initialize all modules: register routes, localizations, etc
modules.populate([
    // Documents
    invoices,
    orders,
    orderConfirmations,
    rfqs,
    delivery_notes,
    propertyBills, // customer-specific (DBImmo)
    universal,
    list_of_services,
    voice_agents,
    assistants,
    mailboxes,
    // Shared
    analytics,
    chat,
    forms,
    masterdata,
    // Account management
    login,
    teams,
    users,
    customer,
]);

const translations = import.meta.glob('../locales/**/*.json', { eager: true });

const legacyLocaleResources = Object.entries(translations).reduce((acc, [path, module]: [string, any]) => {
    const [, lang, file] = path.match(/\.\.\/locales\/(\w+)\/(.+)\.json/) || [];
    if (!lang || !file) return acc;

    acc[lang] = acc[lang] || {};
    acc[lang][file] = module.default;
    return acc;
}, {});

const localeResources = merge(legacyLocaleResources, modules.getLocales()) as Resource;
const dateLocales = {
    en: enUS,
    de: de,
    fr: fr,
    pl: pl,
    ja: ja,
    ko: ko,
    it: it,
    cs: cs,
    sk: sk,
    sl: sl,
    hu: hu,
    ro: ro,
    hr: hr,
    sr: sr,
    es: es,
};

void i18n
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        debug: false,
        // lng: DEFAULT_LANGUAGE, -> should be recognized by the language detector
        fallbackLng: 'en',
        supportedLngs: Object.keys(localeResources),
        ns: Object.keys(localeResources),
        defaultNS: 'translation',
        interpolation: {
            escapeValue: false,
        },
        load: 'languageOnly',
        returnEmptyString: true,
        resources: localeResources,
    });

// Set initial default locale
setDefaultOptions({ locale: dateLocales[i18n.language] || dateLocales.en });

// Update date-fns locale when language changes
i18n.on('languageChanged', (lng) => {
    const langCode = lng.split('-')[0]; // Handle 'en-US' format
    setDefaultOptions({ locale: dateLocales[langCode] || dateLocales.en });
});

const InitializedApp = initAppInsights(() => (
    <ApolloProvider client={client}>
        <App />
    </ApolloProvider>
));

if (process.env.RUN_ENV != 'development') {
    Sentry.init({
        dsn: 'https://cdeb31cc54d4ece8d3d688e4231c4c98@sentry.workist.com/7',
        environment: process.env.RUN_ENV as string,
    } as any);
}

createRoot(document.getElementById('root')!).render(
    <React.Suspense fallback={<AppLoadingScreen />}>
        <Toast.Provider>
            <InitializedApp />
            <Toast.Viewport />
        </Toast.Provider>
    </React.Suspense>
);
